import React, { useState, useMemo } from "react";

const PatientLookupPage = () => {
  // Initial form state
  const [mobileNo, setMobileNo] = useState("");
  const [loyaltyCardNumber, setLoyaltyCardNumber] = useState("");

  // API responses
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitDetails, setVisitDetails] = useState([]);

  // Loading & error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setPatient(null);
    setVisits([]);
    setVisitDetails([]);

    try {
      // === API 1: Get patient by mobile & loyalty card ===
      const zeenatLabApi = `https://api.zeenatlabs.com/api/ERP/GetByMobile?mobileNo=${encodeURIComponent(
        mobileNo
      )}&loyaltyCardNumber=${encodeURIComponent(loyaltyCardNumber)}`;
      const responseZeenatLabApi = await fetch(zeenatLabApi);

      if (!responseZeenatLabApi.ok) {
        throw new Error(
          `Failed to fetch patient ${responseZeenatLabApi.status} ${responseZeenatLabApi.statusText}`
        );
      }

      const patientList = await responseZeenatLabApi.json();
      if (!Array.isArray(patientList) || patientList.length === 0) {
        throw new Error("No patient found with the provided details.");
      }

      const patientData = patientList[0];
      setPatient(patientData);
      const patientId = patientData.PatientId;

      // === API 2: Get visits using PatientId ===
      const getVisitOfPatient = `https://api.zeenatlabs.com/api/ERP/GetPatientVisits?patientId=${encodeURIComponent(
        patientId
      )}`;
      const responseGetVisitOfPatient = await fetch(getVisitOfPatient);

      if (!responseGetVisitOfPatient.ok) {
        throw new Error(
          `Failed to fetch patient visits: ${responseGetVisitOfPatient.status} ${responseGetVisitOfPatient.statusText}`
        );
      }

      const visitList = await responseGetVisitOfPatient.json();
      setVisits(Array.isArray(visitList) ? visitList : []);

      // === API 3: Get visit details (line items) using PatientId ===
      const getPatientLineItems = `https://api.zeenatlabs.com/api/ERP/GetPatientTestDetails?patientId=${encodeURIComponent(
        patientId
      )}`;
      const responseGetPatientLineItems = await fetch(getPatientLineItems);

      if (!responseGetPatientLineItems.ok) {
        throw new Error(
          `Failed to fetch visit details: ${responseGetPatientLineItems.status} ${responseGetPatientLineItems.statusText}`
        );
      }

      const detailsList = await responseGetPatientLineItems.json();
      setVisitDetails(Array.isArray(detailsList) ? detailsList : []);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Group visit details by PatientOrderId for efficient lookup
  const visitDetailsGrouped = useMemo(() => {
    const grouped = {};
    visitDetails.forEach((detail) => {
      const orderId = detail.PatientOrderId;
      if (!grouped[orderId]) grouped[orderId] = [];
      grouped[orderId].push(detail);
    });
    return grouped;
  }, [visitDetails]);

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Segoe UI, Arial, sans-serif",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <h2 style={{ color: "#2c3e50" }}>Patient Lookup</h2>

      {/* Initial Search Form */}
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#f9f9f9",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "24px",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "4px",
              fontWeight: "bold",
            }}
          >
            Mobile Number:
          </label>
          <input
            type="text"
            value={mobileNo}
            onChange={(e) => setMobileNo(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "8px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "4px",
              fontWeight: "bold",
            }}
          >
            Loyalty Card Number:
          </label>
          <input
            type="text"
            value={loyaltyCardNumber}
            onChange={(e) => setLoyaltyCardNumber(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "8px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            backgroundColor: loading ? "#bdc3c7" : "#3498db",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Searching..." : "Search Patient"}
        </button>
      </form>

      {error && (
        <div
          style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          ❌ {error}
        </div>
      )}

      {/* Patient Details */}
      {patient && (
        <div
          style={{
            background: "#e8f4fc",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "24px",
          }}
        >
          <h3 style={{ marginTop: 0, color: "#2980b9" }}>Patient Details</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <strong>Patient ID:</strong> {patient.PatientId}
            </div>
            <div>
              <strong>Patient No:</strong> {patient.PatientNo}
            </div>
            <div>
              <strong>Name:</strong> {patient.FirstName || "N/A"}
            </div>
            <div>
              <strong>Mobile:</strong> {patient.Mobile1}
            </div>
            <div>
              <strong>Loyalty Card:</strong> {patient.LoyaltyCardNumber}
            </div>
            <div>
              <strong>DOB:</strong>{" "}
              {patient.DOB ? new Date(patient.DOB).toLocaleDateString() : "N/A"}
            </div>
            <div>
              <strong>Age:</strong> {patient.Age}
            </div>
            <div>
              <strong>Gender Profile ID:</strong> {patient.GenderProfileId}
            </div>
            <div>
              <strong>Created On:</strong>{" "}
              {patient.CreatedOn
                ? new Date(patient.CreatedOn).toLocaleString()
                : "N/A"}
            </div>
          </div>
        </div>
      )}

      {/* Visits Table with Nested Details */}
      {visits.length > 0 && (
        <div>
          <h3 style={{ color: "#27ae60" }}>Patient Visits ({visits.length})</h3>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "15px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#2ecc71", color: "white" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      border: "1px solid #ddd",
                    }}
                  >
                    Order ID
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      border: "1px solid #ddd",
                    }}
                  >
                    Order No
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      border: "1px solid #ddd",
                    }}
                  >
                    Visit Date & Time
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      border: "1px solid #ddd",
                    }}
                  >
                    Total Net
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      border: "1px solid #ddd",
                    }}
                  >
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => {
                  const details =
                    visitDetailsGrouped[visit.PatientOrderId] || [];
                  return (
                    <React.Fragment key={visit.PatientOrderId}>
                      <tr style={{ backgroundColor: "#f9f9f9" }}>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          {visit.PatientOrderId}
                        </td>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          {visit.PatientOrderNo}
                        </td>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          {visit.PatientOrderOn
                            ? new Date(visit.PatientOrderOn).toLocaleString()
                            : "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            border: "1px solid #ddd",
                            textAlign: "right",
                          }}
                        >
                          {visit.TotalNet?.toFixed(2)}
                        </td>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          {details.length} item{details.length !== 1 ? "s" : ""}
                        </td>
                      </tr>

                      {details.length > 0 && (
                        <tr>
                          <td
                            colSpan="5"
                            style={{ padding: "0", border: "1px solid #ddd" }}
                          >
                            <div
                              style={{
                                padding: "12px",
                                backgroundColor: "#f0f8ff",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "8px",
                                  fontSize: "15px",
                                  padding: "6px",
                                  backgroundColor: "#e8f4fc",
                                  borderRadius: "4px",
                                }}
                              >
                                Order Details:{" "}
                                <span style={{ fontWeight: "normal" }}>
                                  Order ID: {visit.PatientOrderId} | Order No:{" "}
                                  {visit.PatientOrderNo}
                                </span>
                              </div>
                              <table
                                style={{
                                  width: "100%",
                                  marginTop: "8px",
                                  fontSize: "14px",
                                }}
                              >
                                <thead>
                                  <tr style={{ backgroundColor: "#d6eaf8" }}>
                                    <th
                                      style={{
                                        padding: "6px",
                                        textAlign: "left",
                                      }}
                                    >
                                      Service
                                    </th>
                                    <th
                                      style={{
                                        padding: "6px",
                                        textAlign: "right",
                                      }}
                                    >
                                      Price
                                    </th>
                                    <th
                                      style={{
                                        padding: "6px",
                                        textAlign: "left",
                                      }}
                                    >
                                      Report
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.map((detail, idx) => (
                                    <tr
                                      key={idx}
                                      style={{
                                        backgroundColor:
                                          idx % 2 === 0 ? "#ffffff" : "#f0f8ff",
                                      }}
                                    >
                                      <td style={{ padding: "6px" }}>
                                        {detail.PatientOServiceNamerderNo ||
                                          "N/A"}
                                      </td>
                                      <td
                                        style={{
                                          padding: "6px",
                                          textAlign: "right",
                                        }}
                                      >
                                        {detail.Price?.toFixed(2)}
                                      </td>
                                      <td style={{ padding: "6px" }}>
                                        {detail.ReportDownloadLink ? (
                                          <a
                                            href={detail.ReportDownloadLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: "#3498db",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            View Report
                                          </a>
                                        ) : (
                                          "N/A"
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && !error && <p>Fetching data...</p>}
    </div>
  );
};

export default PatientLookupPage;
